import { ReactElement, useEffect, useRef, useState } from 'react'
import { GetSurveyResponse, SurveyQuestion, SurveyAnswerMap } from '../../models/global.types'
import { SurveyQuestionType } from '../../enums/enums'
import apis from '../../libs/services'
import { useAuth } from '../../hooks/useAuth'
import { Button, LoadingSpinner, Text } from '../atoms'
import { useKeyboardControls } from '../../contexts/KeyboardControlsContext'
import { ControlHighlight, KeyboardControl } from '../molecules/KeyboardControls'
import { HighlightEffect } from '../../enums/KeyboardControls'

interface SurveyProps {
  surveyId: string
  onFinish?: () => void
}

const kbControlsLayer = 'survey'
const surveyButtonId = 'survey-button-next'
const getQuestionInputId = (questionId: string): string => `survey-input-${questionId}`
const surveyPageClass = 'bg-dopage-black min-h-screen flex flex-col items-center justify-center font-primary'
const surveyCardClass = 'bg-dopage-charcoalGray rounded-lg p-8 w-[600px] h-[750px] flex items-center justify-center'

function Survey ({ surveyId, onFinish }: SurveyProps): ReactElement {
  const { user } = useAuth()
  const keyboardControls = useKeyboardControls()
  const [surveyData, setSurveyData] = useState<GetSurveyResponse | null>(null)
  const [currentIndex, setCurrentIndex] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [answers, setAnswers] = useState<Record<string, SurveyAnswerMap>>({})
  const [shouldFocusInput, setShouldFocusInput] = useState(true)
  const textAnswerDebounceRef = useRef<NodeJS.Timeout | null>(null)

  const storageKeyAnswers = `survey_${surveyId}_${user?.id ?? 'anonymous'}_answers`
  const storageKeyIndex = `survey_${surveyId}_${user?.id ?? 'anonymous'}_index`

  // Push layer for keyboard controls
  useEffect(() => {
    keyboardControls.pushActiveLayer(kbControlsLayer)
    return () => keyboardControls.resetDefaultLayer()
  }, [keyboardControls])

  // Load answers and index from localStorage on mount
  useEffect(() => {
    const savedAnswers = localStorage.getItem(storageKeyAnswers)
    if (savedAnswers != null) {
      try {
        setAnswers(JSON.parse(savedAnswers))
      } catch {
        // Invalid JSON, ignore
      }
    }
    const savedIndex = localStorage.getItem(storageKeyIndex)
    if (savedIndex != null) {
      const index = parseInt(savedIndex, 10)
      if (!isNaN(index)) setCurrentIndex(index)
    }
  }, [storageKeyAnswers, storageKeyIndex])

  // Save index when it changes
  useEffect(() => {
    localStorage.setItem(storageKeyIndex, String(currentIndex))
  }, [storageKeyIndex, currentIndex])

  // Fetch survey data
  useEffect(() => {
    const fetchSurvey = async (): Promise<void> => {
      try {
        const res = await apis.getSurvey(surveyId)
        setSurveyData(res.data)
      } catch (err: any) {
        setError(err?.message || 'Failed to load survey')
      } finally {
        setLoading(false)
      }
    }

    fetchSurvey()
  }, [surveyId])

  // Set focus on question input when question changes
  useEffect(() => {
    if (!shouldFocusInput || !surveyData) return
    const inputId = getQuestionInputId(surveyData.questions[currentIndex].id)
    keyboardControls.setFocusedElement(inputId, kbControlsLayer)
    setShouldFocusInput(false)
  }, [shouldFocusInput, surveyData, currentIndex, keyboardControls])

  const updateAnswer = (questionId: string, answer: SurveyAnswerMap): void => {
    const newAnswers = { ...answers, [questionId]: answer }
    setAnswers(newAnswers)
    localStorage.setItem(storageKeyAnswers, JSON.stringify(newAnswers))
  }

  if (loading) {
    return (
      <div className={surveyPageClass}>
        <div className={surveyCardClass}>
          <LoadingSpinner />
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className={surveyPageClass}>
        <div className={surveyCardClass}>
          <Text text={error} size='LG' className='text-red-500' />
        </div>
      </div>
    )
  }

  if (!surveyData || surveyData.questions.length === 0) {
    return (
      <div className={surveyPageClass}>
        <div className={surveyCardClass}>
          <Text text='No survey found' size='LG' className='text-dopage-lightGreen' />
        </div>
      </div>
    )
  }

  const currentQuestion = surveyData.questions[currentIndex]
  const isLastQuestion = currentIndex === surveyData.questions.length - 1
  const progressPercent = ((currentIndex + 1) / surveyData.questions.length) * 100

  const hasValidAnswer = (): boolean => {
    const answer = answers[currentQuestion.id]
    if (answer == null) return false
    return ((answer.textResponse?.trim().length ?? 0) > 0) || ((answer.choiceIds?.length ?? 0) > 0)
  }
  const isNextDisabled = currentQuestion.isRequired && !hasValidAnswer()

  const handleNext = (): void => {
    if (isLastQuestion) return
    const answer = answers[currentQuestion.id]
    if (answer) {
      // Fire and forget - don't block UI
      apis.submitSurveyAnswer(surveyId, currentQuestion.id, {
        textResponse: answer.textResponse,
        choiceIds: answer.choiceIds
      })
    }
    setCurrentIndex(prev => prev + 1)
    setShouldFocusInput(true)
  }

  const handleFinish = async (): Promise<void> => {
    const answer = answers[currentQuestion.id]
    if (answer) {
      await apis.submitSurveyAnswer(surveyId, currentQuestion.id, {
        textResponse: answer.textResponse,
        choiceIds: answer.choiceIds
      })
    }
    await apis.completeSurvey(surveyId)
    localStorage.removeItem(storageKeyAnswers)
    localStorage.removeItem(storageKeyIndex)
    onFinish?.()
  }

  // NOTE(Jesus): this is an initial incomplete reference question type implementation
  const renderQuestionInputSectionText = (question: SurveyQuestion): ReactElement => {
    const currentAnswer = answers[question.id]?.textResponse ?? ''
    const inputId = getQuestionInputId(question.id)

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>): void => {
      const value = e.target.value
      if (textAnswerDebounceRef.current) clearTimeout(textAnswerDebounceRef.current)
      textAnswerDebounceRef.current = setTimeout(() => {
        updateAnswer(question.id, { textResponse: value, choiceIds: [] })
      }, 300)
    }

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>): void => {
      if (e.key === 'Tab') {
        e.preventDefault()
        if (e.shiftKey) {
          keyboardControls.focusPreviousElement()
        } else {
          keyboardControls.focusNextElement()
        }
      }
    }

    return (
      <ControlHighlight
        active={keyboardControls.isEnabled && keyboardControls.isElementFocused(inputId)}
        highlightEffect={HighlightEffect.InputOutline}
      >
        <KeyboardControl layer={kbControlsLayer} manualFocusHandling>
          <textarea
            key={question.id}
            id={inputId}
            defaultValue={currentAnswer}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            placeholder='Type your answer here...'
            className='w-full h-40 p-4 bg-dopage-black text-dopage-lightGreen border border-dopage-tealGray rounded-lg resize-none focus:outline-none focus:border-dopage-primary'
          />
        </KeyboardControl>
      </ControlHighlight>
    )
  }

  // NOTE(Jesus): implement these methods for specific question type support
  const renderQuestionInputSectionSingleSelect = (question: SurveyQuestion): ReactElement => {
    return <div className='text-dopage-disabled'>[SingleSelect input placeholder]</div>
  }

  const renderQuestionInputSectionMultiSelect = (question: SurveyQuestion): ReactElement => {
    return <div className='text-dopage-disabled'>[MultiSelect input placeholder]</div>
  }

  const renderQuestionInputSectionLikert = (question: SurveyQuestion): ReactElement => {
    return <div className='text-dopage-disabled'>[Likert input placeholder]</div>
  }

  const renderQuestionInputSection = (question: SurveyQuestion): ReactElement => {
    switch (question.questionType) {
      case SurveyQuestionType.Text:
        return renderQuestionInputSectionText(question)
      case SurveyQuestionType.SingleSelect:
        return renderQuestionInputSectionSingleSelect(question)
      case SurveyQuestionType.MultiSelect:
        return renderQuestionInputSectionMultiSelect(question)
      case SurveyQuestionType.Likert:
        return renderQuestionInputSectionLikert(question)
      default:
        return <div className='text-dopage-disabled'>[Unknown question type]</div>
    }
  }

  return (
    <div className={surveyPageClass}>
      <div className='bg-dopage-charcoalGray rounded-t-lg w-[600px] h-[750px] flex flex-col relative overflow-hidden'>
        <div className='p-8 flex-1 flex flex-col'>
          <Text
            text={currentQuestion.text}
            size='LG'
            className='text-dopage-lightGreen mb-8'
          />

          <div className='flex-1'>
            {renderQuestionInputSection(currentQuestion)}
          </div>

          <div className='flex justify-center'>
            <ControlHighlight
              active={keyboardControls.isEnabled && keyboardControls.isElementFocused(surveyButtonId)}
              highlightEffect={HighlightEffect.ButtonHexOutline}
            >
              <KeyboardControl layer={kbControlsLayer}>
                <Button
                  id={surveyButtonId}
                  disabled={isNextDisabled}
                  onClick={isLastQuestion ? handleFinish : handleNext}
                  className='px-8 py-3 rounded-full font-semibold'
                >
                  <span>{isLastQuestion ? 'Finish' : 'Next'}</span>
                </Button>
              </KeyboardControl>
            </ControlHighlight>
          </div>
        </div>

        <div className='h-4 bg-dopage-tealGray w-full'>
          <div
            className='h-full bg-dopage-primary transition-all duration-300'
            style={{ width: `${progressPercent}%` }}
          />
        </div>
      </div>
    </div>
  )
}

export default Survey
